import { useMemo } from 'react'
import { useWindowDimensions } from 'react-native'
import { useTooltipConstants } from '../constants'
import type { LayoutType, TTooltipAlign, TTooltipSide } from '../types'
import { PositionAlignment, PositionDirection } from '../types/enums'

interface UseOptimalTooltipPositionProps {
  triggerLayout: LayoutType
  contentLayout: LayoutType
  preferredSide: TTooltipSide
  preferredAlign: TTooltipAlign
  tooltipGap: number
}

interface TooltipPosition {
  side: TTooltipSide
  align: TTooltipAlign
}

export function useOptimalTooltipPosition({
  triggerLayout,
  contentLayout,
  preferredSide,
  preferredAlign,
  tooltipGap,
}: UseOptimalTooltipPositionProps): TooltipPosition {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const tooltipConstants = useTooltipConstants()

  return useMemo(() => {
    if (!triggerLayout || !contentLayout) {
      return {
        side: preferredSide,
        align: preferredAlign,
      }
    }

    const HALF_DIVISOR = 2
    const triggerX = triggerLayout.x
    const triggerY = triggerLayout.y
    const triggerWidth = triggerLayout.width
    const triggerHeight = triggerLayout.height
    const contentHeight = contentLayout.height

    const getContentWidth = (side: TTooltipSide): number => {
      return side === PositionDirection.LEFT || side === PositionDirection.RIGHT
        ? tooltipConstants.FIXED_LATERAL_WIDTH
        : contentLayout.width
    }

    const isPositionValid = (
      side: TTooltipSide,
      align: TTooltipAlign
    ): boolean => {
      let tooltipX = 0
      let tooltipY = 0
      const currentContentWidth = getContentWidth(side)

      const sideMap = {
        [PositionDirection.TOP]: () => {
          tooltipY = triggerY - contentHeight - tooltipGap
        },
        [PositionDirection.BOTTOM]: () => {
          tooltipY = triggerY + triggerHeight + tooltipGap
        },
        [PositionDirection.LEFT]: () => {
          tooltipX = triggerX - currentContentWidth - tooltipGap
          tooltipY = triggerY + (triggerHeight - contentHeight) / HALF_DIVISOR
        },
        [PositionDirection.RIGHT]: () => {
          tooltipX = triggerX + triggerWidth + tooltipGap
          tooltipY = triggerY + (triggerHeight - contentHeight) / HALF_DIVISOR
        },
      }
      sideMap[side]?.()

      if (side === PositionDirection.TOP || side === PositionDirection.BOTTOM) {
        const triggerCenterX = triggerX + triggerWidth / HALF_DIVISOR
        const alignMap = {
          [PositionAlignment.START]: () => {
            const minLeft = tooltipConstants.SCREEN_PADDING
            const maxLeft =
              screenWidth -
              currentContentWidth -
              tooltipConstants.SCREEN_PADDING
            tooltipX = Math.max(minLeft, Math.min(triggerX, maxLeft))
          },
          [PositionAlignment.MIDDLE]: () => {
            const idealLeft =
              triggerCenterX - currentContentWidth / HALF_DIVISOR
            const minLeft = tooltipConstants.SCREEN_PADDING
            const maxLeft =
              screenWidth -
              currentContentWidth -
              tooltipConstants.SCREEN_PADDING
            tooltipX = Math.max(minLeft, Math.min(idealLeft, maxLeft))
          },
          [PositionAlignment.END]: () => {
            const idealLeft = triggerX + triggerWidth - currentContentWidth
            const minLeft = tooltipConstants.SCREEN_PADDING
            tooltipX = Math.max(minLeft, idealLeft)
          },
        }
        alignMap[align]?.()
      }

      if (side === PositionDirection.LEFT || side === PositionDirection.RIGHT) {
        const alignMap = {
          [PositionAlignment.START]: () => {
            tooltipY = triggerY
          },
          [PositionAlignment.MIDDLE]: () => {
            tooltipY = triggerY + (triggerHeight - contentHeight) / HALF_DIVISOR
          },
          [PositionAlignment.END]: () => {
            tooltipY = triggerY + triggerHeight - contentHeight
          },
        }
        alignMap[align]?.()
      }

      const isVerticallyValid =
        tooltipY >= tooltipConstants.SCREEN_PADDING &&
        tooltipY + contentHeight <=
          screenHeight - tooltipConstants.SCREEN_PADDING

      const isHorizontallyValid =
        tooltipX >= tooltipConstants.SCREEN_PADDING &&
        tooltipX + currentContentWidth <=
          screenWidth - tooltipConstants.SCREEN_PADDING

      return isVerticallyValid && isHorizontallyValid
    }

    const sides: TTooltipSide[] = Object.values(PositionDirection)
    const aligns: TTooltipAlign[] = Object.values(PositionAlignment)

    const getValidAlign = (
      side: TTooltipSide,
      preferredAlign: TTooltipAlign
    ): TTooltipAlign => {
      if (side === PositionDirection.LEFT || side === PositionDirection.RIGHT) {
        return PositionAlignment.MIDDLE
      }
      return preferredAlign
    }

    const validPreferredAlign = getValidAlign(preferredSide, preferredAlign)

    if (isPositionValid(preferredSide, validPreferredAlign)) {
      return {
        side: preferredSide,
        align: validPreferredAlign,
      }
    }

    for (const align of aligns) {
      const validAlign = getValidAlign(preferredSide, align)
      if (
        validAlign !== validPreferredAlign &&
        isPositionValid(preferredSide, validAlign)
      ) {
        return {
          side: preferredSide,
          align: validAlign,
        }
      }
    }

    for (const side of sides) {
      const validAlign = getValidAlign(side, preferredAlign)
      if (side !== preferredSide && isPositionValid(side, validAlign)) {
        return {
          side,
          align: validAlign,
        }
      }
    }

    for (const side of sides) {
      for (const align of aligns) {
        const validAlign = getValidAlign(side, align)
        if (isPositionValid(side, validAlign)) {
          return {
            side,
            align: validAlign,
          }
        }
      }
    }

    const spaceAbove = triggerY
    const spaceBelow = screenHeight - (triggerY + triggerHeight)
    const spaceLeft = triggerX
    const spaceRight = screenWidth - (triggerX + triggerWidth)

    const maxSpace = Math.max(spaceAbove, spaceBelow, spaceLeft, spaceRight)
    let fallbackSide: TTooltipSide

    if (maxSpace === spaceAbove) {
      fallbackSide = PositionDirection.TOP
    } else if (maxSpace === spaceBelow) {
      fallbackSide = PositionDirection.BOTTOM
    } else if (maxSpace === spaceLeft) {
      fallbackSide = PositionDirection.LEFT
    } else {
      fallbackSide = PositionDirection.RIGHT
    }

    let fallbackAlign: TTooltipAlign = PositionAlignment.MIDDLE

    if (
      fallbackSide === PositionDirection.TOP ||
      fallbackSide === PositionDirection.BOTTOM
    ) {
      const contentWidth = getContentWidth(fallbackSide)
      const triggerCenterX = triggerX + triggerWidth / HALF_DIVISOR
      const idealLeft = triggerCenterX - contentWidth / HALF_DIVISOR

      if (idealLeft < tooltipConstants.SCREEN_PADDING) {
        fallbackAlign = PositionAlignment.START
      } else if (
        idealLeft + contentWidth >
        screenWidth - tooltipConstants.SCREEN_PADDING
      ) {
        fallbackAlign = PositionAlignment.END
      } else {
        fallbackAlign = PositionAlignment.MIDDLE
      }
    }

    return {
      side: fallbackSide,
      align: getValidAlign(fallbackSide, fallbackAlign),
    }
  }, [
    triggerLayout,
    contentLayout,
    preferredSide,
    preferredAlign,
    tooltipGap,
    screenWidth,
    screenHeight,
    tooltipConstants.SCREEN_PADDING,
    tooltipConstants.FIXED_LATERAL_WIDTH,
  ])
}
